import { useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { useTranslation } from '@fluxora/i18n';
import { Alert, Button, Spinner } from '@fluxora/ui';
import { STOREFRONT_PATHS } from '@rheora/enrollment-contract';

import { useFunnelSnapshotQuery } from '../api/funnel.queries';
import { useResumeContinueMutation, useResumeContinueSkipMutation } from '../api/resume.queries';
import { replaceLocation } from '../services/browser-navigation';
import { decideContinueEntry } from '../services/resume-continue.service';

/**
 * `/resume/continue` (E-CRM04) — where every "resume your enrollment" link lands.
 *
 * The link's GET does NOT redeem the single-use token: corporate mail scanners
 * prefetch every link in an email and would spend it. The BFF holds the token
 * server-side and sends the visitor here; pressing Continue
 * (`POST /bff/resume/continue`) redeems it and answers where to go, which is a
 * FULL navigation (the hydration gate must read the newly seeded session).
 *
 * Nothing about the lead is shown — nothing has been redeemed yet. If no token is
 * held (a refresh after use, opened directly) the page goes to `/resume/expired`.
 * A `<div>`, not a `<main>`: the storefront layout owns the one main landmark.
 */
export function ResumeContinuePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snapshot = useFunnelSnapshotQuery();
  const press = useResumeContinueMutation();
  const skip = useResumeContinueSkipMutation();

  const entry = decideContinueEntry({
    isPending: snapshot.isPending,
    isError: snapshot.isError,
    snapshot: snapshot.data,
  });

  // A successful press always leaves the page (to the landing, or to /resume/expired).
  useEffect(() => {
    if (press.data !== undefined) replaceLocation(press.data.redirectTo);
  }, [press.data]);

  if (entry.kind === 'loading') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner size="lg" label={t('shop.resume.continue.loading')} />
      </div>
    );
  }
  // Only before the press: afterwards the session no longer holds the token by
  // design, and the navigation above is already on its way.
  if (entry.kind === 'expired' && press.data === undefined && !press.isPending) {
    return <Navigate to={STOREFRONT_PATHS.resumeExpired} replace />;
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-5 px-4 py-10">
      <h1 className="text-xl font-bold text-primary">{t('shop.resume.continue.heading')}</h1>
      <p className="text-sm text-muted">{t('shop.resume.continue.body')}</p>

      {press.isError ? (
        <Alert variant="danger" title={t('shop.resume.continue.failedTitle')}>
          {t('shop.resume.continue.failedBody')}
        </Alert>
      ) : null}

      <Button
        variant="primary"
        shape="pill"
        isLoading={press.isPending || press.data !== undefined}
        onClick={() => press.mutate()}
      >
        {t('shop.resume.continue.continue')}
      </Button>

      <Link
        to="/"
        className="text-sm font-medium text-primary underline"
        onClick={(e) => {
          e.preventDefault();
          skip.mutate(undefined, { onSettled: () => navigate('/', { replace: true }) });
        }}
      >
        {t('shop.resume.continue.startOver')}
      </Link>
    </div>
  );
}
